import React from "react";
import { useNavigate } from "react-router-dom";
import { PhoneCall, PhoneMissed, Voicemail, MessageSquareWarning, Activity, MessageSquare, UserPlus, CheckCircle2, DollarSign } from "lucide-react";
import { fmtMoney, timeAgo, SectionCard, SectionError } from "./dashUtils";

const StatChip = ({ icon: Icon, label, value, cls, to, navigate, testid }) => (
  <button onClick={() => to && navigate(to)} className="rounded-lg border border-border px-2.5 py-2 text-left hover:border-blue-500/40 transition-colors" data-testid={testid}>
    <div className={`flex items-center gap-1 text-[10px] mb-0.5 ${cls}`}><Icon className="w-3 h-3" /> {label}</div>
    <p className="text-base font-bold text-foreground font-['Manrope']">{value}</p>
  </button>
);

export const CommunicationsCard = ({ comms, locationFiltered, onRetry }) => {
  const navigate = useNavigate();
  if (!comms) return null;
  return (
    <SectionCard title="Calls & Messages" icon={PhoneCall} testid="communications-card"
      action={locationFiltered && !comms.error ? <span className="text-[10px] text-muted-foreground bg-muted px-1.5 py-0.5 rounded">account-wide</span> : null}>
      {comms.error ? (
        <SectionError label="call data" onRetry={onRetry} />
      ) : (
        <>
          <div className="grid grid-cols-3 gap-2">
            <StatChip icon={PhoneCall} label="Inbound" value={comms.inbound_calls} cls="text-blue-500" to="/dashboard/calls" navigate={navigate} testid="comm-inbound" />
            <StatChip icon={CheckCircle2} label="Answered" value={comms.answered_calls} cls="text-emerald-600" to="/dashboard/calls" navigate={navigate} testid="comm-answered" />
            <StatChip icon={PhoneMissed} label="Missed" value={comms.missed_calls} cls="text-red-500" to="/dashboard/calls" navigate={navigate} testid="comm-missed" />
            <StatChip icon={MessageSquare} label="Unread SMS" value={comms.unread_conversations} cls="text-cyan-600" to="/dashboard/messages" navigate={navigate} testid="comm-unread" />
            <StatChip icon={Voicemail} label="Voicemails" value={comms.voicemails_unread} cls="text-violet-500" to="/dashboard/calls" navigate={navigate} testid="comm-vm" />
            <StatChip icon={MessageSquareWarning} label="Failed Msgs" value={comms.failed_messages} cls="text-amber-600" to="/dashboard/messages" navigate={navigate} testid="comm-failed" />
          </div>
          {comms.avg_call_duration > 0 && (
            <p className="text-[10px] text-muted-foreground mt-2">Avg answered call: {Math.floor(comms.avg_call_duration / 60)}m {comms.avg_call_duration % 60}s</p>
          )}
          {(comms.recent || []).length > 0 && (
            <div className="mt-3 pt-2 border-t border-border/50 space-y-1.5">
              {(comms.recent || []).slice(0, 4).map((r, i) => (
                <button key={i} onClick={() => navigate(r.type === "sms" ? "/dashboard/messages" : "/dashboard/calls")}
                  className="w-full flex items-start gap-2 text-left rounded-md px-1.5 py-1 hover:bg-muted/50 transition-colors" data-testid={`comm-recent-${i}`}>
                  {r.type === "missed_call"
                    ? <PhoneMissed className="w-3.5 h-3.5 text-red-500 mt-0.5 shrink-0" />
                    : <MessageSquare className="w-3.5 h-3.5 text-cyan-600 mt-0.5 shrink-0" />}
                  <span className="min-w-0 flex-1">
                    <span className="text-xs text-foreground block truncate">
                      {r.type === "missed_call" ? `Missed call — ${r.who || "Unknown"}` : `${r.who || "Customer"}: ${r.text || "SMS"}`}
                    </span>
                    <span className="text-[10px] text-muted-foreground">{timeAgo(r.at)}</span>
                  </span>
                </button>
              ))}
            </div>
          )}
        </>
      )}
    </SectionCard>
  );
};

const ICONS = {
  job_completed: { icon: CheckCircle2, cls: "text-emerald-600 bg-emerald-500/10" },
  payment: { icon: DollarSign, cls: "text-green-600 bg-green-500/10" },
  new_customer: { icon: UserPlus, cls: "text-blue-500 bg-blue-500/10" },
  missed_call: { icon: PhoneMissed, cls: "text-red-500 bg-red-500/10" },
  sms: { icon: MessageSquare, cls: "text-cyan-600 bg-cyan-500/10" },
};

export const ActivityFeed = ({ activity, financialAccess }) => {
  const navigate = useNavigate();
  const list = activity || [];
  return (
    <SectionCard title="Recent Activity" icon={Activity} testid="activity-feed">
      {list.length === 0 ? (
        <p className="text-sm text-muted-foreground text-center py-6">No activity in this period</p>
      ) : (
        <div className="space-y-1 max-h-[340px] overflow-y-auto pr-1">
          {list.map((a, i) => {
            const cfg = ICONS[a.type] || ICONS.sms;
            const Icon = cfg.icon;
            const text = a.type === "job_completed" ? `Job completed — ${a.title || ""}`
              : a.type === "payment" ? `${financialAccess && a.amount ? fmtMoney(a.amount) + " " : ""}payment received · ${a.title || ""}`
              : a.type === "new_customer" ? "New customer"
              : a.type === "missed_call" ? "Missed call"
              : `SMS: ${a.text || ""}`;
            const dest = a.type === "payment" ? "/dashboard/invoices" : a.type === "job_completed" ? `/dashboard/jobs?job=${a.job_id}` : a.type === "new_customer" ? `/dashboard/customers${a.customer_id ? `?open=${a.customer_id}` : ""}` : a.type === "sms" ? "/dashboard/messages" : "/dashboard/calls";
            return (
              <button key={i} onClick={() => navigate(dest)} className="w-full flex items-start gap-2.5 rounded-lg px-2 py-1.5 text-left hover:bg-muted/50 transition-colors" data-testid={`activity-${i}`}>
                <span className={`w-6 h-6 rounded-md flex items-center justify-center shrink-0 ${cfg.cls}`}><Icon className="w-3.5 h-3.5" /></span>
                <span className="min-w-0 flex-1">
                  <span className="text-xs text-foreground block truncate">{text}</span>
                  <span className="text-[10px] text-muted-foreground truncate block">
                    {[a.who, a.location].filter(Boolean).join(" · ")}{a.at ? ` · ${timeAgo(a.at)}` : ""}
                  </span>
                </span>
              </button>
            );
          })}
        </div>
      )}
    </SectionCard>
  );
};
